/**
 * @file ChannelTile.tsx
 * @description Плитка одного канала в блоке «Каналы продаж» (phase17.1, шаг 8).
 *
 * Тумблер публикации (inline), счётчик отличий (overrideCount), для MARKETPLACE —
 * статус синка/модерации + бейдж needsResync. Клик «Открыть» → drawer карточки.
 * Touch-target ≥ 48px (mobile-first).
 */

import { AlertTriangle, ChevronRight, RefreshCw } from "lucide-react";

import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import { Switch } from "../../../../components/ui/switch";
import { CHANNEL_LABELS, type ChannelSummary } from "../../../../lib/api-channel-listings";

const SYNC_LABELS: Record<string, string> = {
  NOT_SYNCED: "не синкан",
  PENDING: "ожидает",
  SYNCED: "синхронизирован",
  ERROR: "ошибка синка",
};

interface Props {
  item: ChannelSummary;
  busy: boolean;
  onToggle: (next: boolean) => void;
  onOpen: () => void;
}

export function ChannelTile({ item, busy, onToggle, onOpen }: Props) {
  const isMarketplace = item.channelType === "MARKETPLACE";
  const label = CHANNEL_LABELS[item.channel] ?? item.channel;

  return (
    <div className="border border-outline-variant rounded-lg p-3 flex flex-col gap-2 min-h-[48px]">
      {/* Заголовок + тумблер */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`size-2.5 rounded-full shrink-0 ${item.isPublished ? "bg-primary" : "bg-outline"}`}
            aria-hidden
          />
          <span className="font-medium text-on-surface truncate">{label}</span>
        </div>
        <Switch
          checked={item.isPublished}
          onCheckedChange={onToggle}
          disabled={busy}
          aria-label={`Публиковать в ${label}`}
        />
      </div>

      {/* Метаданные */}
      <div className="flex items-center gap-2 flex-wrap text-xs text-on-surface-variant">
        <span>{item.isPublished ? "опубликован" : "черновик"}</span>
        <span aria-hidden>·</span>
        <span className="inline-flex items-center gap-1">
          {item.overrideCount} {pluralOverrides(item.overrideCount)}
        </span>
        {isMarketplace && item.syncStatus ? (
          <>
            <span aria-hidden>·</span>
            <span className={item.syncStatus === "ERROR" ? "text-destructive inline-flex items-center gap-1" : ""}>
              {item.syncStatus === "ERROR" ? <AlertTriangle className="size-3" /> : null}
              {SYNC_LABELS[item.syncStatus] ?? item.syncStatus}
            </span>
          </>
        ) : null}
        {isMarketplace && item.needsResync ? (
          <Badge variant="secondary" className="gap-1 py-0">
            <RefreshCw className="size-3" /> ре-синк
          </Badge>
        ) : null}
      </div>

      <Button variant="outline" size="sm" className="mt-1 justify-between" onClick={onOpen}>
        Открыть карточку
        <ChevronRight className="size-4" />
      </Button>
    </div>
  );
}

function pluralOverrides(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "отличие";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "отличия";
  return "отличий";
}
